import { useMemo, useState } from 'react';
import { COUNTRIES, US, type Country } from '../../data/countries';
import { CustomSelect, type Option } from './CustomSelect';

export type PhoneValue = { country: Country; national: string; e164: string; valid: boolean };

export function formatNational(digits: string, iso: string): string {
  const d = digits.replace(/\D/g, '');
  if (iso === 'US' || iso === 'CA') {
    const a = d.slice(0, 3);
    const b = d.slice(3, 6);
    const c = d.slice(6, 10);
    if (d.length > 6) return `(${a}) ${b}-${c}`;
    if (d.length > 3) return `(${a}) ${b}`;
    if (d.length > 0) return `(${a}`;
    return '';
  }
  return d.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
}

export function PhoneField({
  value,
  onChange,
  invalid,
}: {
  value: PhoneValue;
  onChange: (v: PhoneValue) => void;
  invalid?: boolean;
}) {
  const [touched, setTouched] = useState(false);

  const options: Option[] = useMemo(
    () => COUNTRIES.map((c) => ({ value: c.iso, label: `${c.flag}  ${c.name}`, hint: c.dial })),
    [],
  );

  const setCountry = (iso: string) => {
    const country = COUNTRIES.find((c) => c.iso === iso) ?? US;
    emit(country, value.national);
  };

  const setNational = (raw: string) => {
    // US/CA are fixed at 10 digits; other countries vary, so allow up to 15.
    const cap = value.country.iso === 'US' || value.country.iso === 'CA' ? 10 : 15;
    const digits = raw.replace(/\D/g, '').slice(0, cap);
    emit(value.country, digits);
  };

  const emit = (country: Country, digits: string) => {
    // Strict 10 digits for US/CA (the primary market); a sensible 6–15 range
    // elsewhere so valid international numbers are never wrongly rejected.
    const valid =
      country.iso === 'US' || country.iso === 'CA'
        ? digits.length === 10
        : digits.length >= 6 && digits.length <= 15;
    onChange({
      country,
      national: digits,
      e164: valid ? `${country.dial}${digits}` : '',
      valid,
    });
  };

  const showError = (touched || invalid) && value.national.length > 0 && !value.valid;

  return (
    <div>
      <div className="flex gap-2">
        <div className="w-[124px] shrink-0">
          <CustomSelect
            options={options}
            value={value.country.iso}
            onChange={setCountry}
            searchable
            searchPlaceholder="Search country or code"
            ariaLabel="Country dialling code"
            renderButton={(sel) => {
              const c = COUNTRIES.find((x) => x.iso === sel?.value) ?? value.country;
              return (
                <span className="flex items-center gap-1.5">
                  <span className="text-lg leading-none">{c.flag}</span>
                  <span className="font-body text-sm font-semibold text-plum">{c.dial}</span>
                </span>
              );
            }}
            buttonClassName={`flex w-full cursor-pointer items-center justify-between rounded-2xl border bg-white px-3 py-3.5 transition-colors ${
              showError ? 'border-terracotta' : 'border-stone hover:border-terracotta/50'
            }`}
          />
        </div>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={formatNational(value.national, value.country.iso)}
          onChange={(e) => setNational(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={value.country.iso === 'US' ? '(555) 123-4567' : 'Phone number'}
          className={`w-full rounded-2xl border bg-white px-4 py-3.5 font-body text-plum outline-none transition-colors placeholder:text-plum/40 ${
            showError ? 'border-terracotta shadow-glow' : 'border-stone focus:border-terracotta focus:shadow-glow'
          }`}
        />
      </div>
      {showError && (
        <p className="mt-1.5 font-body text-xs text-terracotta-deep">
          {value.country.iso === 'US' || value.country.iso === 'CA'
            ? 'Please enter a valid 10-digit number so your specialist can reach you.'
            : 'Please enter a valid phone number so your specialist can reach you.'}
        </p>
      )}
    </div>
  );
}

export const emptyPhone = (): PhoneValue => ({ country: US, national: '', e164: '', valid: false });
